import { supabase } from '../lib/supabaseClient';

export type ErrorSeverity = 'critical' | 'error' | 'warning';
export type ErrorType = 'frontend' | 'backend' | 'database' | 'network' | 'auth';

interface ErrorLogData {
  errorType: ErrorType;
  severity: ErrorSeverity;
  message: string;
  stackTrace?: string;
  context?: Record<string, unknown>;
  userId?: string;
}

const CRITICAL_ERROR_PATTERNS = [
  /failed to fetch/i,
  /network error/i,
  /timeout/i,
  /supabase.*error/i,
  /database.*error/i,
  /authentication.*failed/i,
  /cannot connect/i,
];

export function isCriticalError(error: Error | string): boolean {
  const message = typeof error === 'string' ? error : error.message;
  return CRITICAL_ERROR_PATTERNS.some(pattern => pattern.test(message));
}

export function determineErrorType(error: Error | string): ErrorType {
  const message = typeof error === 'string' ? error : error.message;

  if (/fetch|network|timeout|connect/i.test(message)) return 'network';
  if (/auth|login|session/i.test(message)) return 'auth';
  if (/database|sql|supabase/i.test(message)) return 'database';
  if (/backend|api|server/i.test(message)) return 'backend';

  return 'frontend';
}

export async function logError(data: ErrorLogData): Promise<void> {
  try {
    const { error } = await supabase
      .from('error_logs')
      .insert({
        error_type: data.errorType,
        severity: data.severity,
        message: data.message,
        stack_trace: data.stackTrace,
        context: {
          ...data.context,
          userAgent: navigator.userAgent,
          url: window.location.href,
          timestamp: new Date().toISOString(),
        },
        user_id: data.userId || null,
      });

    if (error) {
      console.error('Failed to log error to database:', error);
    }
  } catch (e) {
    console.error('Error tracking service failed:', e);
  }
}

export async function trackError(
  error: Error | string,
  context?: Record<string, unknown>,
  userId?: string
): Promise<void> {
  const message = typeof error === 'string' ? error : error.message;
  const stack = typeof error === 'string' ? undefined : error.stack;

  const severity: ErrorSeverity = isCriticalError(error) ? 'critical' : 'error';
  const errorType = determineErrorType(error);

  await logError({
    errorType,
    severity,
    message,
    stackTrace: stack,
    context,
    userId,
  });
}

export async function trackCriticalError(
  message: string,
  context?: Record<string, unknown>,
  userId?: string
): Promise<void> {
  await logError({
    errorType: 'frontend',
    severity: 'critical',
    message,
    context,
    userId,
  });
}

export async function trackNetworkError(
  message: string,
  endpoint?: string,
  userId?: string
): Promise<void> {
  await logError({
    errorType: 'network',
    severity: 'critical',
    message,
    context: { endpoint },
    userId,
  });
}
